"use client";

import { useRef } from "react";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useSectionNavTheme } from "@/app/hooks/useSectionNavTheme";
import Image from "next/image";
import { Card } from "../shared/card";

gsap.registerPlugin(ScrollTrigger);

export const AboutUs = () => {
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  useSectionNavTheme({
    trigger: "#about-us",
    isDark: false,
  });

  useGSAP(() => {
    const section = sectionRef.current;
    const heading = headingRef.current;
    const content = contentRef.current;

    if (!section || !heading || !content) return;

    // Main content animation
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: "top 80%",
        end: "top 30%",
        scrub: true,
      },
    });

    tl.to(heading, {
      opacity: 1,
      y: 0,
      duration: 0.5,
    }).to(
      content,
      {
        opacity: 1,
        y: 0,
        duration: 0.5,
      },
      "-=0.3"
    );
  }, []);

  return (
    <section
      id="about-us"
      ref={sectionRef}
      className="relative py-20 lg:py-32 bg-gradient-to-br from-black via-40% via-haizea-dark to-haizea-green overflow-hidden"
    >
      {/* Decorative Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="float-element absolute bottom-20 -right-64 w-96 h-96 bg-gradient-to-br from-haizea-green to-haizea-green/[20] rounded-full blur-3xl"
          data-speed="0.35"
        ></div>
        <Image
          src="/images/logo-icon-white.webp"
          alt="River Basin"
          className="absolute -left-[400px] xl:-left-[200px]  -top-[100px] xl:-top-[200px] opacity-10 w-[900px] h-[900px] xl:w-[1200px] xl:h-[1200px] max-w-none select-none object-cover"
          data-speed="1.10"
          width={1800}
          height={1800}
        />
      </div>

      <div className="container relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2
              ref={headingRef}
              className="text-4xl md:text-5xl lg:text-6xl mb-4 lg:mb-8 leading-snug font-light opacity-0 translate-y-10 bg-gradient-to-r from-haizea-blue via-haizea-green to-haizea-orange bg-clip-text text-transparent"
            >
              About Us
            </h2>
          </div>

          {/* Main Mission Statement */}
          <div ref={contentRef} className="opacity-0 translate-y-10">
            <Card className="max-w-4xl mx-auto mb-16">
              <p className="text-xl md:text-2xl lg:text-3xl leading-relaxed text-slate-700 dark:text-slate-50 mb-8 text-center">
                At Haizea, we combine deep scientific know-how with advanced AI
                to make satellite and climate data truly relevant.
              </p>
              <p className="text-lg md:text-xl leading-relaxed text-slate-600 dark:text-slate-100 text-center">
                With many years of experience in large-scale geospatial data
                analysis and AI model development, we help government, research
                and industry accelerate their climate and environmental projects
                — solving the hard data problems that hold others back.
              </p>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
